import styles from '../styles/post.module.scss';
import { container } from 'tsyringe';
import { useState } from 'react';
import Flag from '@ui-kit/components/flag';
import { FormData, IForumService, PlayerInfo, Thread as IThread } from '../model/interfaces';
import { ReactComponent as DotsIcon } from '@ui-kit/icons/dots-three.svg';
import { RolesForPosts } from '../constants/roles';
import { useHistory } from 'react-router-dom';
import { ResponseCode } from '@base/utils/constants';
import { t, useBehaviorSubject } from '@base/utils/helpers';
import FormWithEditor from './formWithEditor';
import { IMessage } from '@base/features/message/message';

function Thread(props: { thread: IThread; creator: PlayerInfo; refreshList: () => Promise<void> }) {
  const history = useHistory();
  const { thread, creator, refreshList } = props;
  const forumService: IForumService = container.resolve('forumService');
  const message: IMessage = container.resolve('message');
  const [isEdit, setEdit] = useState(false);
  const isModer = useBehaviorSubject(forumService.isModerating);
  const [loading, setLoading] = useState(false);

  const deleteThread = () => {
    setLoading(true);
    forumService
      .deleteThread(thread.ThreadID)
      .then((res) => {
        if (res.code === ResponseCode.SUCCESS) {
          message.success(res.message ? res.message : `Topic deleted`, () => history.goBack());
        } else {
          message.warning(res.message!);
        }
      })
      .catch((e) => {
        message.error(e.message);
      })
      .finally(() => setLoading(false));
  };

  const editThread = (data: FormData) => {
    setLoading(true);
    forumService
      .editThread(thread.ThreadID, data.subject, data.message, data.state, thread.Priority)
      .then((res) => {
        if (res.code === ResponseCode.SUCCESS) {
          message.success(res.message! ? res.message! : `Topic edited`, () => refreshList());
        } else {
          message.warning(res.message!);
        }
      })
      .catch((e) => {
        message.error(e.message);
      }).finally(() => setLoading(false));
  };

  if (!creator) return null;

  return (
    <>
      <div className={styles.post}>
        <div className={styles.userInfo}>
          <h3>{thread.Creator}</h3>
          <div className="avatar-overlay" style={{ height: '70px', width: '70px' }}>
            <img
              src={`https://www.gamecolony.com/cgi-bin/get_avatar.plx?uid=${thread.UserID}&size=2`}
              alt="avatar"
            />
          </div>
          <div className={styles.info}>
            <Flag countryCode={creator.cCode} />
            <p className={styles[`priority-${thread.Priority}`]}>{RolesForPosts[thread.Priority]}</p>
            {creator.nGames && <p>{`${creator.nGames} games`}</p>}
            {creator.nWon && <p>{`${creator.nWon} won`}</p>}
          </div>
        </div>
        <div className={styles.postInfo}>
          <div className={styles.title}>
            <p>{thread.Topic}</p>
            <p>{thread.CreationTime}</p>
          </div>
          <div className={styles.userMsg} dangerouslySetInnerHTML={{ __html: thread.Message }} />

          {isModer && (
            <div className={styles.tooltip}>
              <div className={styles.editTooltip}>
                <DotsIcon height="28px" width="auto" />
                <div className={styles.tooltipBody}>
                  <button onClick={() => setEdit(!isEdit)}>{t('edit')}</button>
                  <button onClick={() => deleteThread()}>{t('delete')}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isEdit && (
        <FormWithEditor
          initInputValue={thread.Topic}
          initMessageValue={thread.Message}
          titleForm={t("edit_topic")}
          placeholderInput={t("topic")}
          placeholderEditor={t("your_messsage")}
          submit={editThread}
          loading={loading}
          threadState={thread.State}
          buttonTitle={t("edit")}
        />
      )}
    </>
  );
}

export default Thread;
